/**
 * Код NCS → цвет на экране (Сергей, 28.10: цвет эмали — только по коду NCS
 * или из палитры). Приближённо: оттенок — смесь двух соседних основных
 * цветов NCS, затем доли белого, чёрного и хроматической части по коду.
 * Для производства всё равно нужен физический образец или веер.
 */

const ELEMENTARY: Record<"Y" | "R" | "B" | "G", [number, number, number]> = {
  Y: [255, 211, 0],
  R: [196, 2, 51],
  B: [0, 135, 189],
  G: [0, 159, 107]
};
const ORDER = ["Y", "R", "B", "G"] as const;

const NCS_CODE = /^(?:NCS\s*)?(?:S\s*)?(\d{2})(\d{2})\s*-?\s*(N|[YRBG](?:\s*(\d{2})\s*([YRBG]))?)$/i;

export interface NcsColor {
  blackness: number;
  chromaticness: number;
  /** Оттенок, например «Y50R»; «N» — нейтральный серый. */
  hue: string;
}

export const parseNcs = (code: string | undefined): NcsColor | null => {
  const match = NCS_CODE.exec(String(code ?? "").trim().replace(/\s+/g, " "));
  if (!match) return null;
  const blackness = Number(match[1]);
  const chromaticness = Number(match[2]);
  if (blackness + chromaticness > 100) return null;
  const hue = match[3]!.toUpperCase().replace(/\s+/g, "");
  if (hue.length === 4) {
    const from = ORDER.indexOf(hue[0] as typeof ORDER[number]);
    const to = ORDER.indexOf(hue[3] as typeof ORDER[number]);
    // Оттенок только между соседними основными: Y→R, R→B, B→G, G→Y.
    if ((from + 1) % 4 !== to) return null;
  }
  return { blackness, chromaticness, hue: hue === "N" ? "N" : hue };
};

const hex2 = (value: number) => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, "0");

/** Код NCS → #RRGGBB; null — код не распознан. */
export const ncsToHex = (code: string | undefined) => {
  const ncs = parseNcs(code);
  if (!ncs) return null;
  const black = ncs.blackness / 100;
  const chroma = ncs.hue === "N" ? 0 : ncs.chromaticness / 100;
  const white = Math.max(0, 1 - black - chroma);
  let hueRgb: [number, number, number] = [255, 255, 255];
  if (ncs.hue !== "N") {
    const from = ELEMENTARY[ncs.hue[0] as keyof typeof ELEMENTARY];
    const share = ncs.hue.length === 4 ? Number(ncs.hue.slice(1, 3)) / 100 : 0;
    const to = ncs.hue.length === 4 ? ELEMENTARY[ncs.hue[3] as keyof typeof ELEMENTARY] : from;
    hueRgb = [0, 1, 2].map((index) => from[index]! * (1 - share) + to[index]! * share) as [number, number, number];
  }
  // Белый у NCS чуть тёплый и не «экранный» #FFF; чёрный — не абсолютный.
  const paper = [246, 245, 240];
  const ink = [28, 28, 30];
  const rgb = [0, 1, 2].map((index) => paper[index]! * white + ink[index]! * black + hueRgb[index]! * chroma);
  return `#${rgb.map(hex2).join("")}`.toUpperCase();
};

/** Код в принятом написании: «NCS S 2005-Y50R». */
export const formatNcs = (code: string | undefined) => {
  const ncs = parseNcs(code);
  if (!ncs) return String(code ?? "").trim();
  return `NCS S ${String(ncs.blackness).padStart(2, "0")}${String(ncs.chromaticness).padStart(2, "0")}-${ncs.hue}`;
};
